import { Reference } from '@approvalmax/types';
import { ScrollableArea } from '@approvalmax/ui';
import { Grid } from '@approvalmax/ui/src/components';
import debounce from 'lodash/debounce';
import { statics } from 'modules/common';
import { qBooksConstants } from 'modules/common/constants';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { font } from 'modules/styles';
import { FC, memo, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useUserBetaFeatures } from 'shared/hooks';
import styled from 'styled-components';

import { editRequestField } from '../../../actions';
import { AttachmentsSection, RequesterInstructionSection } from '../../../components';
import { getContext } from '../../../selectors/pageSelectors';
import { getQBooksContext } from '../../../selectors/qbooks';
import ActionsBlock from '../../form/ActionsBlock/ActionsBlock';
import AddressSection from './components/AddressSection/AddressSection';
import ContactInformationSection from './components/ContactInformationSection/ContactInformationSection';
import Files from './components/Files/Files';
import FinanceSection from './components/FinanceSection/FinanceSection';
import { PaymentsSection } from './components/PaymentsSection/PaymentsSection';
import { messages } from './QBOVendorRequest.messages';
import { QBOVendorRequestContextProvider, useQBOVendorRequestContext } from './QBOVendorRequestContext';

const Root = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;
const Toolbar = styled.div`
    flex: none;
    padding: 30px 60px;
    border-bottom: 1px solid #cacccd;
`;

const ToolbarHeader = styled.div`
    display: flex;
    align-items: center;
`;

const ToolbarHeaderText = styled.div`
    flex: 1;
    text-transform: uppercase;
    ${font(20, '#333333', 'standard')}
`;

const StyledScrollableArea = styled(ScrollableArea)`
    flex: 1;
    overflow: hidden;
`;

const Content = styled.div`
    padding: 30px 60px;
`;

interface QBOVendorRequestWithoutReduxProps {
    accounts: domain.QBooksAccount[];
    currencies: Reference[];
    request: domain.QBooksVendorRequest;
}

export const QBOVendorRequestWithoutRedux: FC<QBOVendorRequestWithoutReduxProps> = (props) => {
    const { accounts, currencies, request } = props;

    const { saveAndSubmitVendor, setShowErrors, updateVendor, isSaving } = useQBOVendorRequestContext();
    const context = useSelector(getContext);

    useEffect(() => {
        updateVendor(request.details);
    }, [request.details, updateVendor]);

    const onSaveAndSubmit = (commentText: string, needToCompleteReview: boolean) => {
        setShowErrors(true);
        saveAndSubmitVendor(commentText, needToCompleteReview);
    };

    const { isBelgradeUIFilesUploadSection } = useUserBetaFeatures();

    return (
        <Root>
            <Toolbar>
                <ToolbarHeader>
                    <ToolbarHeaderText>{messages.headerText}</ToolbarHeaderText>

                    <ActionsBlock customSaveAndSubmitRequest={onSaveAndSubmit} isSaving={isSaving} />
                </ToolbarHeader>
            </Toolbar>

            <RequesterInstructionSection instructionText={context.requesterInstruction} />

            <StyledScrollableArea>
                <Content>
                    <ContactInformationSection />

                    <AddressSection />

                    <PaymentsSection />

                    <FinanceSection accounts={accounts} currencies={currencies} />

                    {isBelgradeUIFilesUploadSection ? (
                        <Grid gridTemplateColumns='50%' gap={16}>
                            <AttachmentsSection
                                request={request}
                                extensions={qBooksConstants.qboAllowedFileExtensions}
                            />
                        </Grid>
                    ) : (
                        <Files />
                    )}
                </Content>
            </StyledScrollableArea>
        </Root>
    );
};

const useCurrencies = (currencyDefaultId: string, isMultiCurrencyEnabled: boolean) => {
    const [currencies, setCurrencies] = useState<Reference[]>([]);

    useEffect(() => {
        const companyDefaultCurrency = statics.currency.currencyReferences.find(
            (currency) => currency.id === currencyDefaultId
        );

        isMultiCurrencyEnabled || !companyDefaultCurrency
            ? setCurrencies(statics.currency.currencyReferences)
            : setCurrencies([companyDefaultCurrency]);
    }, [isMultiCurrencyEnabled, currencyDefaultId]);

    return currencies;
};

interface QBOVendorRequestProps {
    request: domain.QBooksVendorRequest;
}

const QBOVendorRequest: FC<QBOVendorRequestProps> = (props) => {
    const { request } = props;

    const dispatch = useDispatch();
    const qBooksContext = useSelector(getQBooksContext);

    const debouncedEditRequestField = debounce(
        <TDetails, TKey extends keyof TDetails = keyof TDetails>(propName: TKey, newValue: TDetails[TKey]) => {
            dispatch(editRequestField<TDetails>(propName, newValue));
        },
        200
    );

    const currencies = useCurrencies(request.companyCurrency, qBooksContext.isMultiCurrencyEnabled);

    return (
        <QBOVendorRequestContextProvider request={request} editRequestField={debouncedEditRequestField}>
            <QBOVendorRequestWithoutRedux accounts={qBooksContext.accounts} currencies={currencies} request={request} />
        </QBOVendorRequestContextProvider>
    );
};

export default memo(QBOVendorRequest);
